import type { AvailableLayer } from '~/api/availableLayers';

// Display-time squash of the digitized branch. The catalog's group grain shows each
// digitized utility (Utilities → Digitized Layers → Electric/Gas/…) as its own row,
// duplicating the normal utility rows. The Library instead shows ONE "Digitized Layers"
// row. Client-side and Library-only: the backend catalog, the layer-config document
// grain, and the map-picker tree are untouched — `setCatalog` keeps the raw catalog.
// Pure helpers, no React / no I/O — unit-testable.

/**
 * The Digitized Layers section node id — the synthetic row's key. Already a valid
 * document node id (the section appears on every constituent's ancestor chain), so
 * analytics `layerId` stays in the shared id space.
 */
export const DIGITIZED_SECTION_KEY = '_toc:Utilities / Digitized Layers';

/** A catalog group belonging to the digitized branch (the section is on its ancestor chain). */
function isDigitizedGroup(layer: AvailableLayer): boolean {
  return layer.ancestors.some((ancestor) => ancestor.key === DIGITIZED_SECTION_KEY);
}

/** The digitized groups' real node ids — what add/remove fan out to. Catalog order. */
export function digitizedConstituentKeys(layers: readonly AvailableLayer[]): string[] {
  return layers.filter(isDigitizedGroup).map((layer) => layer.key);
}

/**
 * One synthetic group-grain row covering the digitized constituents:
 * - `subLayers` are utility-grain with leaf counts — `Electric (3)`, `Gas (2)`, … —
 *   each keyed by that utility's FIRST leaf, so sub-layer icons, the row icon, and the
 *   popover preview all derive from real leaf tiles-config entries through the existing
 *   `subLayers[0].key` rule (no special-casing downstream).
 * - `ancestors` is the constituents' chain minus the section itself (`[Utilities]`),
 *   so `category` renders "Utilities" like every other utility row.
 * - `updated` is the newest constituent's timestamp; `created` the oldest (the branch
 *   exists since its first digitization), `null` when no constituent carries one.
 */
function squashedDigitizedLayer(constituents: readonly AvailableLayer[]): AvailableLayer {
  const [first] = constituents;
  const sectionIndex = first.ancestors.findIndex((ancestor) => ancestor.key === DIGITIZED_SECTION_KEY);
  return {
    key: DIGITIZED_SECTION_KEY,
    label: first.ancestors[sectionIndex].label,
    category: first.category,
    customDataset: false,
    scope: 'civilgrid',
    ancestors: first.ancestors.slice(0, sectionIndex),
    subLayers: constituents
      .filter((group) => group.subLayers.length > 0)
      .map((group) => ({
        key: group.subLayers[0].key,
        label: `${group.label} (${group.subLayers.length})`,
      })),
    updated: constituents.reduce<string | null>(
      (max, group) => (group.updated !== null && (max === null || group.updated > max) ? group.updated : max),
      null,
    ),
    created: constituents.reduce<string | null>(
      (min, group) => (group.created !== null && (min === null || group.created < min) ? group.created : min),
      null,
    ),
  };
}

/**
 * The Library's display catalog: the digitized groups replaced — at the first
 * constituent's position — by the one {@link squashedDigitizedLayer} row. Catalogs with
 * no digitized rows (org-custom slices, orgs without digitization) pass through as-is.
 */
export function squashDigitizedLayers(layers: readonly AvailableLayer[]): AvailableLayer[] {
  const constituents = layers.filter(isDigitizedGroup);
  if (constituents.length === 0) return [...layers];

  const squashed = squashedDigitizedLayer(constituents);
  const result: AvailableLayer[] = [];
  for (const layer of layers) {
    if (!isDigitizedGroup(layer)) result.push(layer);
    else if (layer === constituents[0]) result.push(squashed);
  }
  return result;
}

/**
 * Library membership for the display catalog: the real ids plus the synthetic id when
 * ANY constituent is in the library. Partial states can exist (seeded documents);
 * showing the row as added keeps Remove available to clear them all, and one
 * Remove + Add round-trip normalizes to the full set.
 */
export function withDigitizedMembership(
  libraryLayerIds: ReadonlySet<string>,
  constituentKeys: readonly string[],
): Set<string> {
  const display = new Set(libraryLayerIds);
  if (constituentKeys.some((key) => display.has(key))) display.add(DIGITIZED_SECTION_KEY);
  return display;
}
